import React from 'react';

import amber from '@material-ui/core/colors/amber';
import IconButton from '@material-ui/core/IconButton';
import SnackbarContent, { SnackbarContentProps as MuiSnackbarContentProps } from '@material-ui/core/SnackbarContent';
import { LumaTheme } from 'core/styles/getTheme';
import { withStyles, createStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import classNames from 'classnames';

import { capitalizeFirstLetter } from '@lib/services/stringsService';

const variantIcon = {
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
};

const styles = ({
  custom: {
    palette: { colorAccentPeach, colorSupportMidnight },
  },
  palette: {
    common: { white },
    primary: { dark },
  },
  spacing: { unit },
  breakpoints: { down },
}: LumaTheme) =>
  createStyles({
    root: {
      color: white,
      maxWidth: 'max-content',
      minWidth: 'max-content',
      [down('sm')]: {
        maxWidth: '100vw',
        minWidth: '100vw',
        flexWrap: 'nowrap',
      },
    },
    success: {
      backgroundColor: dark,
    },
    error: {
      backgroundColor: colorAccentPeach,
      color: colorSupportMidnight,
      padding: `0 ${unit * 2}px`,
    },
    info: {
      backgroundColor: dark,
    },
    action: {
      backgroundColor: '#bcd6de',
      color: '#395b7f',
      padding: `0 ${unit * 3}px`,
      boxShadow: 'none',
    },
    actionWrapper: {
      paddingLeft: 2 * unit,
    },
    warning: {
      backgroundColor: amber[700],
    },
    icon: {
      fontSize: 25,
    },
    iconVariant: {
      opacity: 0.9,
      marginRight: unit,
    },
    messageAction: {
      padding: 0,
    },
    messageError: {
      // line-height values works based on font-size values
      lineHeight: 1.4,
    },
    messageWrapper: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 17,
      fontWeight: 500,
    },
    errorEmoji: {
      marginRight: unit,
      fontSize: 3 * unit,
    },
    iconButton: {
      padding: 0,
      paddingRight: 5,
    },
  });

export type SnackbarContentWrapperVariantKey = 'success' | 'warning' | 'error' | 'info' | 'action';

export interface SnackbarContentProps extends MuiSnackbarContentProps {
  classes: any;
  className?: string;
  message: string;
  variant: SnackbarContentWrapperVariantKey;
  onClose?: any;
}

const SnackbarContentWrapper: React.FC<SnackbarContentProps> = ({
  classes,
  className,
  message,
  variant,
  onClose,
  ...other
}) => {
  const getErrorEmoji = (messageError: String) => {
    if (messageError.includes('401')) {
      return '🙈';
    }
    if (messageError.includes('403')) {
      return '🙊';
    }
    return '🙉';
  };
  const errorEmoji = variant === 'error' && getErrorEmoji(message);
  const Icon = (variantIcon as any)[variant];
  return (
    <SnackbarContent
      classes={{
        root: classNames(classes.root, classes[variant], className),
        message: classes[`message${capitalizeFirstLetter(variant)}`],
        action: classes.actionWrapper,
      }}
      aria-describedby="client-snackbar"
      message={
        <span id="client-snackbar" className={classes.messageWrapper}>
          {errorEmoji && <span className={classes.errorEmoji}>{errorEmoji}</span>}
          {!errorEmoji && Icon && <Icon className={classNames(classes.icon, classes.iconVariant)} />}
          <span className={classes.message}>{message}</span>
        </span>
      }
      action={[
        <IconButton key="close" aria-label="Close" color="inherit" onClick={onClose} className={classes.iconButton}>
          <CloseIcon className={classes.icon} />
        </IconButton>,
      ]}
      {...other}
    />
  );
};

export default withStyles(styles)(SnackbarContentWrapper);
